import styled, { css, keyframes } from 'styled-components';

const bounce = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(0.85); }
  50% { transform: translate(-50%, -50%) scale(1); }
`;

const pulseRing = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.6; }
  80% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
`;

export const Container = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  background-color: #000;
  overflow: hidden;
  outline: none;
  user-select: none;
  border-radius: ${({ $borderRadius }) => $borderRadius};

  &:fullscreen {
    aspect-ratio: auto;
    height: 100%;
    border-radius: 0;
  }
`;

export const PlayerWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  & > * {
    width: 100% !important;
    height: 100% !important;
  }
`;

export const ClickShield = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.4);
`;

export const LoaderInner = styled.div`
  position: relative;
  width: 96px;
  height: 96px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 2px solid rgba(190, 22, 34, 0.75);
    opacity: 0;
    animation: ${pulseRing} 1.6s ease-out infinite both;
  }

  &::after {
    animation-delay: 0.8s;
  }
`;

export const Loader = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 96px;
  height: 96px;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.55));
  animation: ${bounce} 1.2s ease-in-out infinite;
`;

export const ErrorOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.72);
`;

export const ErrorMessage = styled.p`
  margin: 0;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  text-align: center;
`;

export const TopOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  padding: ${({ $padding }) => $padding};
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-6px')});
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  pointer-events: none;
`;

export const ControlsOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 8px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.78) 100%);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '6px')});
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const SeekRow = styled.div`
  width: 100%;
  padding: 10px 0 6px;
`;

export const SeekBar = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  cursor: pointer;
`;

export const SeekTrack = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 3px;
`;

export const SeekBuffered = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: rgba(255, 255, 255, 0.45);
  border-radius: 3px;
`;

export const SeekProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #be1622;
  border-radius: 3px;
`;

export const SeekThumb = styled.div`
  position: absolute;
  top: 50%;
  width: 26px;
  height: 26px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  transition: transform 0.15s ease;

  ${SeekBar}:hover & {
    transform: translate(-50%, -50%) scale(1.15);
  }
`;

export const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.65));
`;

export const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ControlButton = styled.button`
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  line-height: 1;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 1px;
  }
`;

export const VolumeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const VolumeSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 80px;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    #fff 0%,
    #fff ${({ $fillPct }) => $fillPct}%,
    rgba(255, 255, 255, 0.35) ${({ $fillPct }) => $fillPct}%,
    rgba(255, 255, 255, 0.35) 100%
  );
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    border: 0;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border: 0;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
  }
`;

export const TimeDisplay = styled.span`
  color: #fff;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  margin-left: 4px;
`;

export const RateGroup = styled.div`
  position: relative;
`;

export const RateButton = styled.button`
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 1px;
  }
`;

export const RateMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  display: flex;
  flex-direction: column;
  background: rgba(20, 20, 20, 0.96);
  border-radius: 6px;
  padding: 4px;
  min-width: 84px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.55);
`;

export const RateOption = styled.button`
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  padding: 6px 10px;
  font-size: 13px;
  text-align: left;
  border-radius: 4px;
  transition: background 0.15s ease;

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(201, 24, 45, 0.55);
    `}

  &:hover {
    background: ${({ $active }) =>
      $active ? 'rgba(201, 24, 45, 0.7)' : 'rgba(255, 255, 255, 0.14)'};
  }
`;

export const CenterPlayHint = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

export const CenterPlayCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 40px;
  backdrop-filter: blur(2px);
`;
